import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import apiClient from '../services/api';
import ActionButton from '../components/ActionButton';
import { useActionLock } from '../hooks/useActionLock';

const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  failed: 'error',
  expired: 'default',
};

const STATUS_ICONS = {
  pending: <HourglassTopOutlinedIcon sx={{ fontSize: 16 }} />,
  processing: <CircularProgress size={14} />,
  completed: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
  failed: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
  expired: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return '—';
  const date = new Date(isoStr);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const InvoiceDownload = () => {
  const today = new Date();
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(todayStr);
  const [dateError, setDateError] = useState('');

  // Current async job (if 202 returned)
  const [currentJob, setCurrentJob] = useState(null);
  const [jobPolling, setJobPolling] = useState(false);

  // Jobs history list
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [downloading, runDownload] = useActionLock();
  const [refreshing, runRefresh] = useActionLock();

  const validateDates = (start, end) => {
    if (!start || !end) return 'Both dates are required.';
    if (start > end) return 'Start date must be before or equal to end date.';
    const diffMs = new Date(end) - new Date(start);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 366) return 'Date range cannot exceed 366 days.';
    return '';
  };

  const handleStartChange = (e) => {
    setStartDate(e.target.value);
    setDateError(validateDates(e.target.value, endDate));
  };

  const handleEndChange = (e) => {
    setEndDate(e.target.value);
    setDateError(validateDates(startDate, e.target.value));
  };

  const triggerZipDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    const err = validateDates(startDate, endDate);
    if (err) { setDateError(err); return; }

    setErrorMsg('');
    setSuccessMsg('');
    setCurrentJob(null);

    try {
      const response = await apiClient.post(
        '/admin/invoices/export/',
        { start_date: startDate, end_date: endDate },
        { responseType: 'blob', validateStatus: (s) => s < 500 }
      );

      if (response.status === 200) {
        // Sync: direct ZIP download
        const contentDisposition = response.headers['content-disposition'] || '';
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : `invoices_${startDate}_to_${endDate}.zip`;
        triggerZipDownload(response.data, filename);
        const count = response.headers['x-invoice-count'] || '';
        setSuccessMsg(`Downloaded ${count ? count + ' invoice(s)' : 'invoices'} successfully.`);
        fetchJobs();
      } else if (response.status === 202) {
        // Async: read JSON from blob
        const text = await response.data.text();
        const job = JSON.parse(text);
        setCurrentJob(job);
        setSuccessMsg('Export job queued. It will be ready to download shortly.');
        fetchJobs();
      } else {
        let msg = 'Export failed. Please try again.';
        try {
          const text = await response.data.text();
          const json = JSON.parse(text);
          msg = json.detail || json.message || json.error || msg;
        } catch (_) {}
        setErrorMsg(msg);
      }
    } catch (error) {
      setErrorMsg(error.message || 'Export failed. Please try again.');
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      const res = await apiClient.get('/admin/invoices/export/');
      const data = res.data;
      const list = Array.isArray(data) ? data : (data?.results || data?.jobs || []);
      setJobs(list);
    } catch (_) {
      // silently fail — jobs list is supplementary
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll current job status until completed/failed/expired
  useEffect(() => {
    if (!currentJob || ['completed', 'failed', 'expired'].includes(currentJob.status)) {
      setJobPolling(false);
      return;
    }
    setJobPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await apiClient.get(`/admin/invoices/export/${currentJob.job_id}/`);
        const updated = res.data;
        setCurrentJob(updated);
        // Also refresh jobs list
        setJobs((prev) =>
          prev.map((j) => (j.job_id === updated.job_id ? updated : j))
        );
        if (['completed', 'failed', 'expired'].includes(updated.status)) {
          clearInterval(interval);
          setJobPolling(false);
        }
      } catch (_) {
        clearInterval(interval);
        setJobPolling(false);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [currentJob?.job_id, currentJob?.status]);

  const handleRefreshJob = async (jobId) => {
    try {
      const res = await apiClient.get(`/admin/invoices/export/${jobId}/`);
      const updated = res.data;
      setJobs((prev) => prev.map((j) => (j.job_id === jobId ? updated : j)));
      if (currentJob?.job_id === jobId) setCurrentJob(updated);
    } catch (_) {}
  };

  const handleDownloadJob = async (jobId) => {
    try {
      const response = await apiClient.get(
        `/admin/invoices/export/${jobId}/download/`,
        { responseType: 'blob' }
      );
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `invoices_export_${jobId}.zip`;
      triggerZipDownload(response.data, filename);
      setSuccessMsg('Invoice ZIP downloaded successfully.');
    } catch (error) {
      setErrorMsg('Failed to download ZIP. The job may have expired.');
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'white',
      '& fieldset': { borderColor: '#E2E8F0' },
      '&:hover fieldset': { borderColor: '#4E83BD' },
      '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
    },
    '& .MuiInputBase-input': { fontSize: '14px', color: '#1F2937' },
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptLongOutlinedIcon sx={{ color: 'white', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}>
              Invoice Download
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', mt: 0.25 }}>
              Download all invoices for a selected date range as a ZIP file
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ mb: 2, borderRadius: 2 }}>
          {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" onClose={() => setErrorMsg('')} sx={{ mb: 2, borderRadius: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {/* Date Range Filter + Export Button */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', mb: 3 }}>
        <Box sx={{ padding: { xs: 2, sm: 2.5 }, backgroundColor: 'white', borderRadius: 2 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 2 }}>
            Select Date Range
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', md: 'flex-end' },
            }}
          >
            {/* Start Date */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarTodayOutlinedIcon sx={{ color: '#4E83BD', fontSize: 16 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  Start Date
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={startDate}
                onChange={handleStartChange}
                inputProps={{ max: todayStr }}
                sx={inputStyles}
              />
            </Box>

            {/* End Date */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarTodayOutlinedIcon sx={{ color: '#4E83BD', fontSize: 16 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  End Date
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={endDate}
                onChange={handleEndChange}
                inputProps={{ min: startDate, max: todayStr }}
                sx={inputStyles}
              />
            </Box>

            {/* Download Button */}
            <Box sx={{ flexShrink: 0, minWidth: { md: '180px' } }}>
              <ActionButton
                variant="contained"
                fullWidth
                loading={downloading}
                onClick={() => runDownload(handleExport)}
                loadingText="Preparing..."
                startIcon={<FolderZipOutlinedIcon />}
                disabled={!!dateError}
                sx={{
                  backgroundColor: '#4E83BD',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  height: '40px',
                  '&:hover': { backgroundColor: '#3A6A99' },
                  '&.Mui-disabled': { backgroundColor: '#B0C4DE', color: 'white' },
                }}
              >
                Download Invoices
              </ActionButton>
            </Box>
          </Box>

          {dateError && (
            <Typography sx={{ fontSize: '12px', color: '#EF4444', mt: 1 }}>
              {dateError}
            </Typography>
          )}

          {/* Selected range display */}
          {!dateError && startDate && endDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                Selected range:
              </Typography>
              <Chip
                label={`${formatDate(startDate)} — ${formatDate(endDate)}`}
                size="small"
                sx={{ fontSize: '12px', backgroundColor: '#EBF5FF', color: '#3A6A99', fontWeight: 600 }}
              />
            </Box>
          )}
        </Box>

        {/* Async Job Status Banner */}
        {currentJob && (
          <>
            <Divider />
            <Box sx={{ padding: { xs: 2, sm: 2.5 }, backgroundColor: '#FAFBFC' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      backgroundColor: '#EBF5FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {jobPolling ? (
                      <CircularProgress size={18} sx={{ color: '#4E83BD' }} />
                    ) : (
                      <FolderZipOutlinedIcon sx={{ color: '#4E83BD', fontSize: 20 }} />
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1F2937' }}>
                      Export Job {jobPolling ? '— Processing...' : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                      {formatDate(currentJob.start_date)} — {formatDate(currentJob.end_date)}
                      {currentJob.expires_at && ` · Expires ${formatDateTime(currentJob.expires_at)}`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={STATUS_ICONS[currentJob.status]}
                    label={currentJob.status}
                    color={STATUS_COLORS[currentJob.status] || 'default'}
                    size="small"
                    sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                  />
                  <Tooltip title="Refresh status">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => runRefresh(() => handleRefreshJob(currentJob.job_id))}
                        disabled={refreshing || jobPolling}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {currentJob.status === 'completed' && (
                    <ActionButton
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadJob(currentJob.job_id)}
                      loadingText="Downloading..."
                      sx={{
                        backgroundColor: '#22C55E',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        '&:hover': { backgroundColor: '#16A34A' },
                      }}
                    >
                      Download ZIP
                    </ActionButton>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Export Jobs History */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {/* Export Jobs header */}
        <Box
          sx={{
            padding: { xs: 1.5, sm: 2 },
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
            Export Jobs
          </Typography>
          <Tooltip title="Refresh list">
            <span>
              <IconButton
                size="small"
                onClick={() => runRefresh(fetchJobs)}
                disabled={refreshing || jobsLoading}
              >
                {jobsLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Info banner — always visible */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 2.5 },
            py: 1,
            backgroundColor: '#F0F7FF',
            borderBottom: '1px solid #DBEAFE',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 15, color: '#3A6A99', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#3A6A99', whiteSpace: 'nowrap' }}>
            Note:
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#3A6A99' }}>
            Export jobs appear here only when the selected range has <strong>more than 100 invoices</strong>. Smaller exports download instantly as a ZIP.
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                {['Date Range', 'Requested At', 'Status', 'Expires At', 'Actions'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontSize: '12px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#4E83BD' }} />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <FolderZipOutlinedIcon sx={{ fontSize: 40, color: '#CBD5E1' }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>
                        No export jobs yet.
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
                        Select a date range above and click <strong>Download Invoices</strong> to get started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow
                    key={job.job_id}
                    sx={{
                      '&:hover': { backgroundColor: '#F8FAFC' },
                      backgroundColor: currentJob?.job_id === job.job_id ? '#EBF5FF' : 'white',
                    }}
                  >
                    <TableCell sx={{ fontSize: '13px', color: '#1F2937', fontWeight: 500 }}>
                      {formatDate(job.start_date)} — {formatDate(job.end_date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', color: '#64748B' }}>
                      {formatDateTime(job.created_at || job.requested_at)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={STATUS_ICONS[job.status]}
                        label={job.status}
                        color={STATUS_COLORS[job.status] || 'default'}
                        size="small"
                        sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '12px' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', color: '#64748B' }}>
                      {formatDateTime(job.expires_at)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {['pending', 'processing'].includes(job.status) && (
                          <Tooltip title="Refresh status">
                            <IconButton size="small" onClick={() => handleRefreshJob(job.job_id)}>
                              <RefreshIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {job.status === 'completed' && (
                          <Tooltip title="Download ZIP">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadJob(job.job_id)}
                              sx={{ color: '#22C55E' }}
                            >
                              <DownloadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {['failed', 'expired'].includes(job.status) && (
                          <Typography sx={{ fontSize: '12px', color: '#94A3B8', lineHeight: '32px' }}>
                            —
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default InvoiceDownload;
